import React from 'react';
import { useTranslation } from 'react-i18next';
import { searchAddressREST } from '../../../../../../common/src/legacy/services/apiRouterService';

// Components
import YesNoQuestion from '../../../ui/advisory-flow/components/shared-components/YesNoQuestion';

// Types
import { Address } from '../../../../../../../types/Common';
import { Change } from '../../../../../../../types/Form';
import { isObjectEmpty } from '../../../../../../common/src/utils/objectUtils';
import { HomeSearchAnswer } from './HomeInputComponent';
import { useBoolean, useBreakpointValue, Flex } from '@wegroup/design-system';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { useFlowsSelector } from '../../../../../../common/src/utils/globalVariables';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import { omit } from 'lodash';
import { isCompanySelector } from '../../../../store/redux/selectors/flowsSliceSelectors';
import store from '../../../../store/redux/store';
import { formatAddress } from '../../../../../../common/src/utils/commonUtils';

interface Props {
  handleBlur: (arg0: Change) => void;
  question: FlowsQuestion;
  nextSubView: (arg0: string) => void;
  setHomeInformation: (arg0: HomeSearchAnswer) => Promise<void>;
  previousQuestionHandler: () => void;
}

const AddressToInsure: React.FC<React.PropsWithChildren<Props>> = ({
  question,
  nextSubView,
  previousQuestionHandler,
  setHomeInformation,
  handleBlur,
}) => {
  const isCompany = isCompanySelector(store.getState());
  const { t } = useTranslation();
  const answers = useFlowsSelector((state) => state.flows.answers);
  const isMobile = !!useBreakpointValue({ base: true, laptop: false });
  const [loading, setLoading] = useBoolean();

  const getCompanyAddress = () => {
    const companySearchAnswer = answers.find(
      ({ id }) => id === 'company.company_search',
    )?.answer as {
      'company.address': Address;
    };

    if (!isObjectEmpty(companySearchAnswer)) {
      return companySearchAnswer?.['company.address'];
    }
    return answers.find(({ id }) => id === 'company.address')?.answer;
  };

  const answerObject =
    ((isCompany
      ? getCompanyAddress()
      : answers.find(({ id }) => id === 'policy_holder.address')
          ?.answer) as Address & { _local: { self: string } }) || {};

  const handleYes = async () => {
    setLoading.on();
    const copiedAnswer = { ...answerObject };
    if (isCompany) {
      const [allResults, status] = await searchAddressREST(
        formatAddress(answerObject),
      );
      if (status === 200) {
        const result = allResults?.items?.[0];
        copiedAnswer._local = { self: result.self };
      }
    }

    await setHomeInformation({
      address: omit(copiedAnswer, '_local'),
      attic: undefined,
      cellar: undefined,
      _local: {
        self: copiedAnswer._local?.self,
      },
    });
    setLoading.off();
    nextSubView('houseDetail');
  };

  const handleNo = () => {
    handleBlur({
      name: question.id,
      value: {},
    });
    nextSubView('pickHome');
  };

  return (
    <AdvisoryFlowMainContainer data-test-id="ProdFac_AddressToInsure">
      <QuestionGroupAssistantQuestion
        question={t(`Is x the address you would like to insure?`, {
          address: formatAddress(answerObject),
        })}
        description={t(
          'If not, you will be able to search for a different address and use that instead',
        )}
      />
      <Flex
        px="4"
        pt="8"
        alignItems="flex-start"
        justifyContent="center"
        flexGrow={1}
      >
        {!isMobile && (
          <YesNoQuestion
            handleYes={handleYes}
            handleNo={handleNo}
            isYesLoading={loading}
          />
        )}
      </Flex>
      <QuestionGroupActions
        onBack={previousQuestionHandler}
        hideContinueButton
        isLoading={loading}
      >
        {isMobile && (
          <YesNoQuestion
            handleYes={handleYes}
            handleNo={handleNo}
            isYesLoading={loading}
          />
        )}
      </QuestionGroupActions>
    </AdvisoryFlowMainContainer>
  );
};

export default AddressToInsure;
